<div class="message-editor" [class.compact]="compact" [class.assist-active]="assistState !== 'closed' || !!assistResult" [class.assist-result-active]="!!assistResult" #editorContainer>
  <!-- Header Toolbar (hidden in compact mode: composers own their submit controls) -->
  @if (!compact) {
  <div class="editor-header">
    <div class="header-left">
      <!-- Mode Toggle Buttons -->
      <div class="mode-toggle">
        <button class="mode-btn" [class.active]="mode === 'edit'" (click)="setMode('edit')" title="Edit Mode">
          <i class="fa-solid fa-edit"></i>
          <span>Edit</span>
        </button>
        <button class="mode-btn" [class.active]="mode === 'diff'" (click)="setMode('diff')" title="View Changes">
          <i class="fa-solid fa-code-compare"></i>
          <span>Changes</span>
        </button>
      </div>

      <!-- Change Indicator -->
      <div class="change-indicator" *ngIf="hasChanges">
        <i class="fa-solid fa-circle"></i>
        <span>Unsaved changes</span>
      </div>
    </div>

    <div class="header-right">
      <!-- Stats -->
      <div class="content-stats">
        <span class="stat">{{ getWordCount() }} words</span>
        <span class="stat">{{ getCharacterCount() }} chars</span>
      </div>
    </div>
  </div>
  }

  <!-- Editor Content Area -->
  <div class="editor-content" [ngSwitch]="mode">

    <!-- EDIT MODE: Quill WYSIWYG Editor (no toolbar) -->
    <div class="edit-pane full" *ngSwitchCase="'edit'">
      <quill-editor
        #quillEditor
        [modules]="quillModules"
        [ngModel]="editedContent"
        (onEditorCreated)="onEditorCreated($event)"
        (onContentChanged)="onEditorContentChange($event)"
        [readOnly]="readOnly"
        format="html"
        theme="snow"
        [placeholder]="placeholder"
        class="quill-container">
      </quill-editor>

      <!-- AI writing assistant popover: action menu / instruction / running / error, under the toolbar -->
      @if (assistState !== 'closed') {
        <div class="assist-anchor">
          <izzy-writing-assist-menu
            [state]="assistState"
            [items]="assistItems"
            [selectedItem]="assistSelectedItem"
            [instruction]="assistInstruction"
            [scopeLabel]="assistScopeLabel"
            [hasText]="assistHasText"
            [error]="assistError"
            [slow]="assistSlow"
            (pick)="assistPick($event)"
            (instructionChange)="assistInstruction = $event"
            (run)="assistRun()"
            (back)="assistBackToMenu()"
            (retry)="assistRetry()"
            (discard)="assistDiscard()">
          </izzy-writing-assist-menu>
        </div>
      }

      <!-- Inline result: the suggestion is already in the text; highlight it and offer Keep / Undo / Try again -->
      @if (assistResult) {
        @if (assistHighlightBox) {
          <div class="assist-highlight" aria-hidden="true"
               [style.top.px]="assistHighlightBox.top" [style.left.px]="assistHighlightBox.left"
               [style.width.px]="assistHighlightBox.width" [style.height.px]="assistHighlightBox.height"></div>
        }
        <div class="assist-bar" #assistBarEl role="group" aria-label="Izzy suggestion"
             [style.top.px]="assistBarPos.top" [style.left.px]="assistBarPos.left"
             (mousedown)="$event.preventDefault()">
          <span class="assist-bar-label">
            <i class="fa-solid fa-wand-magic-sparkles"></i>
            <strong>{{ assistResult.item.doneLabel }}</strong>
            <span class="assist-bar-count">{{ assistResult.fromWords }} → {{ assistResult.toWords }} words</span>
          </span>
          <span class="assist-bar-actions">
            <button type="button" class="assist-bar-btn primary" (click)="assistKeep()" title="Keep this text"><i class="fa-solid fa-check"></i> Keep</button>
            <button type="button" class="assist-bar-btn" (click)="assistUndo()" title="Put the previous text back"><i class="fa-solid fa-rotate-left"></i> Undo <kbd>{{ undoShortcut }}</kbd></button>
            <button type="button" class="assist-bar-btn quiet" (click)="toggleAssistTryMenu()" title="Run the assistant again on this text">Try again <i class="fa-solid fa-caret-down"></i></button>
            @if (assistTryMenuOpen) {
              <div class="assist-try-menu">
                <button type="button" (click)="assistTryAgain('same')"><i class="fa-solid fa-rotate-right"></i> {{ assistResult.item.label }} again</button>
                <hr>
                <button type="button" (click)="assistTryAgain('shorten')"><i class="fa-solid fa-compress"></i> Make it shorter</button>
                <button type="button" (click)="assistTryAgain('friendly')"><i class="fa-regular fa-face-smile"></i> Make it warmer</button>
                <button type="button" (click)="assistTryAgain('professional')"><i class="fa-solid fa-user-tie"></i> More formal</button>
                <button type="button" (click)="assistTryAgain('custom')"><i class="fa-solid fa-wand-magic-sparkles"></i> Custom instruction…</button>
              </div>
            }
          </span>
        </div>
      }
    </div>

    <!-- DIFF MODE: Track Changes View using DiffViewer component -->
    <!-- Always diff against originalContent (GeneratedReplyContent), not saved drafts -->
    <div class="diff-pane full" *ngSwitchCase="'diff'">
      <izzy-diff-viewer
        [originalContent]="originalContent"
        [modifiedContent]="editedContent"
        [showLegend]="true"
        title="Track Changes">
      </izzy-diff-viewer>
    </div>
  </div>

  <!-- Footer Actions (hidden in compact mode) -->
  @if (!compact) {
  <div class="editor-footer">
    <div class="footer-left">
      <button class="action-btn secondary" (click)="revertToOriginal()" [disabled]="!hasChanges" title="Revert to original">
        <i class="fa-solid fa-rotate-left"></i>
        <span>Revert</span>
      </button>
    </div>
    <div class="footer-right">
      <button class="action-btn cancel" (click)="cancelEdit()" title="Cancel (Esc)">
        <i class="fa-solid fa-xmark"></i>
        <span>Cancel</span>
      </button>
      <!-- Approval deliberately lives OUTSIDE the editor (product call 2026-08-25): staff save
           the draft, exit, and approve from the action bar — no approve-and-send from edit mode. -->
      <button class="action-btn primary" (click)="saveAsDraft()" [disabled]="!hasChanges" title="Save Draft (Ctrl+S)">
        <i class="fa-solid fa-save"></i>
        <span>Save Draft</span>
      </button>
    </div>
  </div>
  }
</div>
